<template>
  <div class="bg-background-light dark:bg-background-dark font-sans text-[#0d121b] antialiased">
    <AppHeader />
    <div class="w-full bg-white dark:bg-[#1a202c]">
      <div class="max-w-[1280px] mx-auto px-4 lg:px-10 py-6 lg:py-8">
        <div class="flex flex-col justify-end overflow-hidden rounded-xl min-h-[200px] lg:min-h-[280px] relative" 
             data-alt="Hubei construction labor association general assembly group photo banner">
          <div class="absolute inset-0 bg-cover bg-center transition-all duration-1000"
               :class="imageLoadedMap['banner'] ? 'blur-0 scale-100' : 'blur-2xl scale-110 opacity-50'"
               style='background-image: linear-gradient(0deg, rgba(15, 23, 42, 0.55) 0%, rgba(15, 23, 42, 0.15) 100%), url("/images/banner/association_overview_banner.png");'>
          </div>
          <img src="/images/banner/association_overview_banner.png" class="hidden" @load="onImageLoad('banner')" />
          <div class="absolute inset-0 bg-black/20 z-10"></div>
          <div class="flex flex-col p-6 lg:p-10 relative z-20">
            <h1 class="text-white tracking-tight text-3xl lg:text-4xl font-bold leading-tight mb-2">协会概况</h1>
            <p class="text-white/90 text-base lg:text-lg font-medium">服务湖北建筑工人，维护职工合法权益，推动行业高质量发展</p>
          </div>
        </div>
      </div>
    </div>
    <div class="max-w-[1280px] mx-auto px-4 lg:px-10 py-12">
      <div class="flex flex-col lg:grid lg:grid-cols-12 gap-8 lg:gap-12 relative">
        <aside class="lg:col-span-3">
          <div class="lg:sticky lg:top-24 space-y-4">
            <div class="bg-white rounded-xl shadow-sm border border-[#e7ebf3] overflow-hidden">
              <div class="p-4 border-b border-[#e7ebf3] bg-[#f8f9fc]">
                <h3 class="text-[#0d121b] text-base font-bold">概况导航</h3>
              </div>
              <nav class="flex lg:flex-col overflow-x-auto lg:overflow-visible p-2 gap-1 hide-scrollbar">
                <a
                  v-for="nav in navItems"
                  :key="nav.key"
                  class="flex-shrink-0 lg:flex-shrink w-auto lg:w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all group"
                  :class="activeSection === nav.key ? 'bg-primary text-white' : 'text-[#0d121b] hover:bg-[#e7ebf3] hover:text-primary'"
                  :href="'#' + nav.key"
                  @click.prevent="scrollToSection(nav.key)"
                >
                  <span
                    class="material-symbols-outlined text-[20px]"
                    :class="activeSection === nav.key ? '' : 'text-[#9ca3af] group-hover:text-primary'"
                  >{{ nav.icon }}</span>
                  <span class="text-sm font-medium">{{ nav.label }}</span>
                </a>
              </nav>
            </div>
            <div class="hidden lg:block bg-gradient-to-br from-primary to-[#0a40b0] rounded-xl p-6 text-white shadow-lg">
              <span class="material-symbols-outlined text-4xl mb-4 opacity-80">support_agent</span>
              <h4 class="font-bold text-lg mb-2">需要帮助?</h4>
              <p class="text-white/80 text-sm mb-4">如果您有任何疑问或需要咨询，请随时联系我们的秘书处。</p>
              <div class="text-sm font-mono opacity-90">027-84881278</div>
              <div class="text-sm font-mono opacity-90">hbsjzlwxh@163.com</div>
            </div>
          </div>
        </aside>
        <main class="lg:col-span-9 space-y-12">
          <section class="bg-white rounded-xl p-6 lg:p-8 shadow-sm border border-[#e7ebf3] scroll-mt-28" id="intro">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-1.5 h-8 bg-primary rounded-full"></div>
              <h2 class="text-2xl font-bold text-[#0d121b]">协会简介</h2>
            </div>
            <div class="block">
              <div v-if="intro && intro.imageUrl" class="w-full lg:w-1/3 lg:float-right lg:ml-8 mb-6 relative overflow-hidden rounded-lg shadow-md aspect-[4/3] bg-slate-50/50 flex items-center justify-center p-2 border border-slate-100">
                <img :alt="intro.imageAlt || '协会简介配图'"
                     class="w-full h-full object-contain transition-all duration-1000 rounded"
                     :class="imageLoadedMap['intro'] ? 'blur-0 scale-100 opacity-100' : 'blur-xl scale-110 opacity-50'"
                     :src="resolveImg(intro.imageUrl)"
                     @load="onImageLoad('intro')"/>
              </div>
              <div class="text-[#4c669a] leading-relaxed space-y-4 text-justify">
                <!-- 正文：后台按「空行」分段，支持内联 HTML（沿用 PostDetail 的口径） -->
                <p v-for="(para, i) in introParagraphs" :key="'p' + i" v-html="para"></p>
                <!-- 理念信息框：后台有值才渲染该条 -->
                <div v-if="introFacts.length" class="bg-blue-50/50 p-4 rounded-lg border border-blue-100 space-y-2 text-sm mt-4">
                  <p v-for="fact in introFacts" :key="fact.label"><strong>{{ fact.label }}：</strong>{{ fact.content }}</p>
                </div>
                <div v-if="intro && (intro.sloganMain || intro.sloganSub)" class="mt-10 p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50/30 border border-blue-100/50 relative overflow-hidden group">
                  <div class="absolute -right-4 -top-4 size-24 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors"></div>
                  <div class="relative z-10 flex flex-col items-center text-center">
                    <span class="material-symbols-outlined text-primary/40 text-4xl mb-4">format_quote</span>
                    <h3 v-if="intro.sloganMain" class="text-xl lg:text-2xl font-black text-primary tracking-widest mb-3 uppercase">{{ intro.sloganMain }}</h3>
                    <div class="h-1 w-12 bg-gradient-to-r from-primary to-transparent rounded-full mb-4"></div>
                    <p v-if="intro.sloganSub" class="text-lg font-bold text-[#1e40af] opacity-90 tracking-wider">{{ intro.sloganSub }}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section class="scroll-mt-28" id="history">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-1.5 h-8 bg-primary rounded-full"></div>
              <h2 class="text-2xl font-bold text-[#0d121b]">发展历程</h2>
            </div>
            <div v-if="historyList.length" class="relative pl-6 lg:pl-10 space-y-12 py-2">
              <!-- Vertical Timeline Line with Gradient -->
              <div class="absolute left-[7px] lg:left-[11px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-transparent"></div>

              <!-- 逐年条目（后台维护）；sortOrder 最小的一条视为「最新」，用高亮样式 -->
              <div v-for="(item, idx) in historyList" :key="item.id" class="relative group">
                <div
                  class="absolute -left-[23px] lg:-left-[35px] top-1.5 size-4 rounded-full border-4 border-white z-10"
                  :class="idx === 0
                    ? 'bg-primary shadow-[0_0_15px_rgba(30,64,175,0.4)] group-hover:scale-125 transition-transform duration-500'
                    : 'bg-slate-300 group-hover:bg-primary transition-colors duration-500 shadow-sm'"
                ></div>
                <div class="flex flex-col sm:flex-row gap-2 sm:gap-10 items-start">
                  <div class="flex flex-col min-w-[100px]">
                    <span
                      class="font-black text-2xl tracking-tighter transition-colors duration-500"
                      :class="idx === 0 ? 'text-primary' : 'text-slate-300 group-hover:text-primary'"
                    >{{ item.year }}</span>
                  </div>
                  <div
                    class="bg-white p-6 rounded-2xl border border-slate-100 flex-1 hover:shadow-xl hover:border-primary/20 transition-all duration-500 relative overflow-hidden group-hover:-translate-y-1"
                    :class="idx === 0
                      ? 'shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)]'
                      : 'shadow-sm'"
                  >
                    <div v-if="idx === 0" class="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl group-hover:bg-primary/10 transition-colors"></div>
                    <h4
                      class="text-slate-800 text-lg mb-2 relative z-10"
                      :class="idx === 0 ? 'font-black' : 'font-bold'"
                    >{{ item.title }}</h4>
                    <p class="text-slate-500 text-sm leading-relaxed relative z-10">{{ item.content }}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section class="scroll-mt-28" id="structure">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-1.5 h-8 bg-primary rounded-full"></div>
              <h2 class="text-2xl font-bold text-[#0d121b]">组织架构</h2>
            </div>
            <div class="bg-[#f8fafc] dark:bg-[#1a202c] p-6 lg:p-12 rounded-2xl shadow-inner border border-[#e7ebf3] dark:border-gray-700 overflow-hidden relative">
              <!-- Background Decorative Grid -->
              <div class="absolute inset-0 opacity-[0.03] pointer-events-none" style="background-image: radial-gradient(#1e40af 1px, transparent 1px); background-size: 24px 24px;"></div>

              <div class="w-full flex flex-col items-center py-8 relative z-10 scale-[0.85] lg:scale-100 origin-top transform-gpu transition-transform">
                <!-- Level 1: General Assembly -->
                <div class="relative flex flex-col items-center group">
                  <div class="bg-gradient-to-br from-[#1e40af] to-[#1e3a8a] text-white px-12 py-4 rounded-2xl font-black text-xl shadow-[0_10px_40px_-10px_rgba(30,64,175,0.5)] border border-white/20 transform group-hover:scale-105 group-hover:shadow-primary/40 transition-all duration-500 cursor-default">
                    {{ orgName('ASSEMBLY') }}
                  </div>
                  <!-- Main Vertical Line -->
                  <div class="h-12 w-0.5 bg-gradient-to-b from-[#1e40af] to-[#2563eb]"></div>
                </div>

                <!-- Level 2 Connector Branch -->
                <div class="relative w-full h-12">
                  <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[480px] h-0.5 bg-[#2563eb]">
                    <!-- Left branch to Council -->
                    <div class="absolute top-0 left-0 h-12 w-0.5 bg-[#2563eb]">
                      <div class="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-[#2563eb]"></div>
                    </div>
                    <!-- Right branch to Supervisory -->
                    <div class="absolute top-0 right-0 h-12 w-0.5 bg-[#2563eb]">
                      <div class="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-[#2563eb]"></div>
                    </div>
                    <!-- Center vertical line (Starts from dotted line, ends with arrow at box) -->
                    <div class="absolute top-[76px] left-1/2 -translate-x-1/2 h-[80px] w-0.5 bg-gradient-to-b from-[#2563eb] to-[#1e40af] z-0">
                       <div class="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-[#1e40af]"></div>
                    </div>
                  </div>
                </div>

                <!-- Level 2: Council & Supervisory -->
                <div class="relative w-full flex justify-center items-center gap-[300px] mb-14">
                  <div class="absolute top-1/2 left-[35%] right-[35%] h-px border-t-2 border-dotted border-blue-200 dark:border-blue-800 -translate-y-1/2 -z-0"></div>
                  
                  <div class="w-[180px] bg-white/80 dark:bg-gray-800/80 backdrop-blur-md text-[#1e40af] dark:text-blue-400 py-4 rounded-2xl font-bold shadow-xl border border-white/50 dark:border-gray-700 hover:border-primary hover:shadow-primary/10 transition-all duration-500 cursor-default z-10 text-center group">
                    {{ orgName('COUNCIL') }}
                    <div class="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-[#2563eb] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                  <div class="w-[180px] bg-white/80 dark:bg-gray-800/80 backdrop-blur-md text-[#1e40af] dark:text-blue-400 py-4 rounded-2xl font-bold shadow-xl border border-white/50 dark:border-gray-700 hover:border-primary hover:shadow-primary/10 transition-all duration-500 cursor-default z-10 text-center group">
                    {{ orgName('SUPERVISORY') }}
                    <div class="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-[#2563eb] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                </div>

                <!-- Level 3: President's Office -->
                <div class="relative flex flex-col items-center group">
                   <div class="bg-gradient-to-br from-[#2563eb] to-[#1e40af] text-white px-12 py-4 rounded-2xl font-black text-lg shadow-2xl border border-white/20 transform group-hover:scale-105 transition-all duration-500">
                     {{ orgName('PRESIDENT_OFFICE') }}
                   </div>
                   <div class="h-12 w-0.5 bg-gradient-to-b from-[#1e40af] to-[#3b82f6]"></div>
                </div>

                <!-- Level 4: Committees & Secretariat -->
                <div class="relative w-full flex flex-col items-center">
                  <div class="relative w-full max-w-5xl flex justify-center items-start h-12">
                    <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[412px] h-0.5 bg-[#3b82f6]">
                      <div class="absolute top-0 left-0 h-12 w-0.5 bg-[#3b82f6]"></div>
                      <div class="absolute top-0 left-1/2 -translate-x-1/2 h-12 w-0.5 bg-[#3b82f6]"></div>
                      <div class="absolute top-0 right-0 h-12 w-0.5 bg-[#3b82f6]"></div>
                    </div>
                  </div>

                  <div class="flex justify-center items-start gap-4 w-full max-w-5xl mb-12">
                    <div class="w-[180px] flex flex-col items-center">
                      <div class="bg-white/90 dark:bg-gray-800 text-slate-700 dark:text-gray-300 px-4 py-4 rounded-2xl font-bold text-sm shadow-lg border border-slate-100 dark:border-gray-700 text-center w-full relative hover:border-primary/40 hover:shadow-xl transition-all duration-500 group">
                        {{ orgName('EXPERT_COMMITTEE') }}
                        <div class="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[7px] border-t-[#3b82f6]"></div>
                      </div>
                    </div>

                    <div class="w-[200px] flex flex-col items-center">
                      <div class="bg-gradient-to-b from-[#1e40af] to-[#1e3a8a] text-white px-8 py-5 rounded-2xl font-black text-lg shadow-2xl border border-white/10 ring-8 ring-blue-50/50 dark:ring-blue-900/10 relative transform hover:scale-110 transition-all duration-500 group cursor-default">
                        {{ orgName('SECRETARIAT') }}
                        <div class="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[7px] border-t-[#3b82f6]"></div>
                      </div>
                    </div>

                    <div class="w-[180px] flex flex-col items-center">
                      <div class="bg-white/90 dark:bg-gray-800 text-slate-700 dark:text-gray-300 px-4 py-4 rounded-2xl font-bold text-sm shadow-lg border border-slate-100 dark:border-gray-700 text-center w-full relative hover:border-primary/40 hover:shadow-xl transition-all duration-500 group">
                        {{ orgName('MEDIATION_COMMITTEE') }}
                        <div class="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[7px] border-t-[#3b82f6]"></div>
                      </div>
                    </div>
                  </div>

                  <div class="relative w-full flex flex-col items-center">
                    <div class="h-12 w-0.5 bg-gradient-to-b from-[#1e3a8a] to-[#3b82f6] relative">
                      <div class="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[7px] border-t-[#3b82f6]"></div>
                    </div>
                    
                    <div class="relative w-full max-w-[1100px] mt-4 px-4">
                      <div class="absolute top-0 left-[5%] right-[5%] h-px bg-gradient-to-r from-transparent via-[#3b82f6] to-transparent"></div>
                      
                      <div class="flex justify-between items-start pt-0">
                        <div v-for="dept in orgDepartments" :key="dept.id" class="flex flex-col items-center flex-1 group">
                          <div class="h-8 w-px bg-slate-200 group-hover:bg-primary transition-colors"></div>
                          <div class="relative w-full flex justify-center px-2">
                            <div 
                              class="relative bg-white dark:bg-gray-800 text-slate-600 dark:text-gray-400 py-8 px-4 rounded-3xl font-black text-[11px] md:text-sm shadow-[0_4px_12px_rgba(0,0,0,0.03)] border border-slate-50 dark:border-gray-700 [writing-mode:vertical-rl] h-40 md:h-52 flex items-center justify-center tracking-[0.4em] leading-relaxed group-hover:text-primary group-hover:border-primary/30 group-hover:shadow-2xl group-hover:shadow-primary/10 transition-all duration-700 w-full overflow-hidden"
                              :class="dept.variant === 'indigo' ? 'bg-gradient-to-b from-indigo-50/50 to-white text-indigo-700 border-indigo-100 shadow-indigo-50' : ''"
                            >
                              <span class="relative z-10">{{ dept.name }}</span>
                              <div class="absolute inset-0 bg-primary/0 group-hover:bg-primary/[0.03] transition-colors"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>


          <section class="scroll-mt-28" id="leadership">
            <div class="flex items-center gap-3 mb-8">
              <div class="w-1.5 h-8 bg-primary rounded-full"></div>
              <h2 class="text-2xl font-bold text-[#0d121b]">领导风采</h2>
            </div>

            <!-- 列表态（无数据则不渲染内容，符合「没有就不显示内容」） -->
            <div v-if="!detailLeader && leaders.length" class="space-y-12">
              <div class="space-y-16">
                <!-- 会长 (Centered with premium treatment) -->
                <div class="flex flex-col items-center">
                  <div
                    class="group relative flex flex-col items-center bg-white dark:bg-gray-800/80 backdrop-blur-xl rounded-[2.5rem] overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-white/50 dark:border-gray-700 hover:shadow-2xl hover:shadow-primary/20 hover:-translate-y-2 transition-all duration-700 w-full max-w-[320px] p-4 cursor-pointer"
                    @click="openLeaderDetail(leaders[0], $event)"
                  >
                    <div class="w-full aspect-[3/4] overflow-hidden rounded-[2rem] bg-slate-50 relative transition-all duration-1000"
                         :class="imageLoadedMap[leaders[0].id] ? 'blur-0 scale-100' : 'blur-2xl scale-110 opacity-50'">
                      <img :alt="leaders[0].name" class="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-110" :src="resolveImg(leaders[0].photoUrl)" @load="onImageLoad(leaders[0].id)"/>
                      <div class="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <div class="absolute bottom-3 right-3 size-9 rounded-full bg-black/40 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span class="material-symbols-outlined text-[18px]">open_in_full</span>
                      </div>
                    </div>
                    <div class="pt-6 pb-4 text-center w-full">
                      <h3 class="text-2xl font-black text-[#0d121b] dark:text-white mb-2 group-hover:text-primary transition-colors">{{ leaders[0].name }}</h3>
                      <div class="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-wider">{{ leaders[0].title }}</div>
                    </div>
                  </div>
                </div>

                <!-- 其他领导 Grid -->
                <div class="max-w-7xl mx-auto">
                  <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-8">
                    <div
                      v-for="item in leaders.slice(1)"
                      :key="item.id"
                      class="group relative flex flex-col items-center bg-white/60 dark:bg-gray-800/60 backdrop-blur-md rounded-3xl overflow-hidden shadow-lg border border-white/40 dark:border-gray-700 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 p-3 cursor-pointer"
                      @click="openLeaderDetail(item, $event)"
                    >
                      <div class="w-full aspect-[3/4] overflow-hidden rounded-2xl bg-slate-50 relative transition-all duration-1000"
                           :class="imageLoadedMap[item.id] ? 'blur-0 scale-100' : 'blur-2xl scale-110 opacity-50'">
                        <img :alt="item.name" class="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" :src="resolveImg(item.photoUrl)" @load="onImageLoad(item.id)"/>
                        <div class="absolute inset-0 bg-gradient-to-b from-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div class="absolute bottom-2 right-2 size-7 rounded-full bg-black/40 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <span class="material-symbols-outlined text-[15px]">open_in_full</span>
                        </div>
                      </div>
                      <div class="pt-4 pb-2 text-center w-full">
                        <h3 class="text-lg font-bold text-[#0d121b] dark:text-white mb-1 group-hover:text-primary transition-colors">{{ item.name }}</h3>
                        <p class="text-[10px] text-slate-400 font-medium leading-tight px-2 h-8 flex items-center justify-center">{{ item.title }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 详情态（原位展开，不做整页跳转；左侧概况导航保持不动） -->
            <div v-else-if="detailLeader" class="leader-detail bg-white rounded-[2.5rem] border border-slate-100 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] p-6 lg:p-10">
              <button
                class="inline-flex items-center gap-1 text-sm font-bold text-slate-400 hover:text-primary transition-colors mb-6"
                @click="closeLeaderDetail"
              >
                <span class="material-symbols-outlined text-[18px]">arrow_back</span> 返回领导风采
              </button>
              <div class="flex flex-col md:flex-row gap-8 lg:gap-12">
                <div
                  ref="detailAvatarRef"
                  class="w-full md:w-[260px] shrink-0 mx-auto md:mx-0 aspect-[3/4] rounded-[2rem] overflow-hidden bg-slate-50 shadow-lg"
                  :style="{ opacity: avatarRevealed ? 1 : 0 }"
                >
                  <img :src="resolveImg(detailLeader.photoUrl)" :alt="detailLeader.name" class="w-full h-full object-cover object-top" />
                </div>
                <div class="flex-1 flex flex-col justify-center">
                  <h3 class="text-3xl font-black text-[#0d121b] mb-3">{{ detailLeader.name }}</h3>
                  <div class="inline-block self-start px-4 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-wider mb-6">{{ detailLeader.title }}</div>
                  <template v-if="detailLeader.bio">
                    <div class="h-0.5 w-16 bg-gradient-to-r from-primary to-transparent rounded-full mb-6"></div>
                    <div class="text-[#4c669a] leading-loose text-justify whitespace-pre-wrap">{{ detailLeader.bio }}</div>
                  </template>
                </div>
              </div>
            </div>
          </section>

          <section class="scroll-mt-28" id="objectives">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-1.5 h-8 bg-primary rounded-full"></div>
              <h2 class="text-2xl font-bold text-[#0d121b]">宗旨与目标</h2>
            </div>
            <div v-if="objectives.length" class="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div
                v-for="obj in objectives"
                :key="obj.id"
                class="group flex flex-col bg-white dark:bg-gray-800 p-8 rounded-[2rem] border border-slate-100 dark:border-gray-700 shadow-[0_4px_25px_rgba(0,0,0,0.02)] hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-2 transition-all duration-500"
                :class="obj.linkUrl ? 'cursor-pointer' : 'cursor-default'"
                @click="goObjective(obj)"
              >
                <div class="size-16 rounded-2xl bg-primary/5 flex items-center justify-center text-primary mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-500">
                  <span class="material-symbols-outlined text-4xl">{{ obj.icon }}</span>
                </div>
                <h3 class="text-xl font-bold text-[#0d121b] dark:text-white mb-4 group-hover:text-primary transition-colors">{{ obj.title }}</h3>
                <p class="text-slate-500 dark:text-slate-400 text-sm leading-relaxed flex-1">
                  {{ obj.description }}
                </p>
                <div v-if="obj.linkUrl" class="mt-5 inline-flex items-center gap-1 text-xs font-bold text-primary">
                  查看详情 <span class="material-symbols-outlined text-[16px]">arrow_forward</span>
                </div>
              </div>
            </div>
          </section>

          <section class="scroll-mt-28" id="achievements">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-1.5 h-8 bg-primary rounded-full"></div>
              <h2 class="text-2xl font-bold text-[#0d121b]">荣誉成就</h2>
            </div>
            <div class="bg-gradient-to-br from-slate-50 to-white dark:from-gray-900 dark:to-gray-800 p-8 md:p-12 rounded-[2.5rem] border border-slate-100 dark:border-gray-700 shadow-inner overflow-hidden relative">
              <div class="absolute -top-24 -right-24 size-96 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
              <div v-if="honors.length" class="grid grid-cols-1 lg:grid-cols-2 gap-8 relative z-10">
                <div
                  v-for="award in honors"
                  :key="award.id"
                  class="group bg-white dark:bg-gray-800 p-5 rounded-[1.25rem] border border-slate-50 dark:border-gray-700 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-2xl hover:border-primary/30 transition-all duration-500"
                  :class="award.imageUrl ? 'cursor-pointer' : ''"
                  @click="goHonor(award)"
                >
                  <div class="flex items-center gap-5">
                    <!-- 已上传证书/奖牌图片 → 显示图片；未上传 → 用图标占位 -->
                    <div
                      v-if="award.imageUrl"
                      class="size-14 shrink-0 rounded-xl overflow-hidden bg-slate-50 border border-slate-100"
                    >
                      <img :src="resolveImg(award.imageUrl)" :alt="award.title" class="w-full h-full object-cover" />
                    </div>
                    <div
                      v-else
                      class="size-14 shrink-0 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-amber-600 group-hover:bg-primary group-hover:text-white transition-all duration-500"
                    >
                      <span class="material-symbols-outlined text-2xl">{{ award.icon || 'emoji_events' }}</span>
                    </div>
                    <div class="flex-1">
                      <div class="inline-block px-2 py-0.5 rounded bg-blue-50 text-primary text-[10px] font-black mb-1.5">{{ award.year }}</div>
                      <h4 class="font-black text-[#0d121b] dark:text-white text-base leading-snug group-hover:text-primary transition-colors">{{ award.title }}</h4>
                      <p class="text-xs text-slate-400 font-medium mt-1">{{ award.org }}</p>
                    </div>
                    <span v-if="award.imageUrl" class="material-symbols-outlined text-[20px] text-slate-300 group-hover:text-primary transition-colors">zoom_in</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
    <!-- 领导头像飞入克隆体（固定定位，仅动画期间可见） -->
    <img
      v-if="flying.show"
      :src="flyingSrc"
      alt=""
      class="fixed pointer-events-none object-cover object-top shadow-2xl"
      :style="flying.style"
    />

    <!-- 荣誉证书 / 奖牌大图预览 -->
    <transition name="fade">
      <div
        v-if="lightboxUrl"
        class="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-6"
        @click="lightboxUrl = ''"
      >
        <button
          class="absolute top-6 right-6 size-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
          @click="lightboxUrl = ''"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
        <img :src="lightboxUrl" alt="荣誉证书" class="max-w-full max-h-full rounded-lg shadow-2xl" @click.stop />
      </div>
    </transition>

    <footer class="bg-white border-t border-[#e7ebf3] py-12 mt-12">
      <div class="max-w-[1280px] mx-auto px-4 lg:px-10">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center gap-3 text-[#0d121b] mb-4">
              <img alt="Logo" class="w-8 h-8 object-contain" src="/logo.png"/>
              <h2 class="text-lg font-bold">湖北省建筑劳务协会</h2>
            </div>
            <p class="text-[#4c669a] text-sm leading-relaxed max-w-sm mb-6">
              弘扬工匠精神，助推行业发展!<br/>
              ——为建筑劳务保驾护航!
            </p>
            <div class="flex gap-4">
              <a class="size-8 rounded-full bg-[#f8f9fc] flex items-center justify-center text-[#4c669a] hover:bg-primary hover:text-white transition-colors" href="#">
                <span class="material-symbols-outlined text-[18px]">share</span>
              </a>
              <a class="size-8 rounded-full bg-[#f8f9fc] flex items-center justify-center text-[#4c669a] hover:bg-primary hover:text-white transition-colors" href="#">
                <span class="material-symbols-outlined text-[18px]">mail</span>
              </a>
            </div>
          </div>
          <div>
            <h3 class="font-bold text-[#0d121b] mb-4">快速链接</h3>
            <ul class="space-y-2 text-sm text-[#4c669a]">
              <li><a class="hover:text-primary" href="https://zjt.hubei.gov.cn/" target="_blank">湖北省住建厅官网</a></li>
            </ul>
          </div>
          <div>
            <h3 class="font-bold text-[#0d121b] mb-4">联系方式</h3>
            <ul class="space-y-3 text-sm text-[#4c669a]">
              <li class="flex items-start gap-2">
                <span class="material-symbols-outlined text-[18px] mt-0.5">location_on</span>
                <span>武汉市汉阳区龙阳大道123号<br/>欧亚达汉阳国际广场A座1803</span>
              </li>
              <li class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[18px]">call</span>
                <span>027-84881278 (电话)</span>
              </li>
              <li class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[18px]">fax</span>
                <span>027-84881278 (传真)</span>
              </li>
              <li class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[18px]">mail</span>
                <span>hbsjzlwxh@163.com</span>
              </li>
              <li class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[18px]">markunread_mailbox</span>
                <span>430000</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="border-t border-[#e7ebf3] mt-8 pt-8 text-center text-xs text-[#9ca3af]">
          <p>©2018 湖北省建筑劳务协会保留所有权利。 | <a href="https://beian.miit.gov.cn/" target="_blank" class="hover:text-primary">鄂ICP备18023234号-1</a></p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue';
import aboutService from '@/api/aboutService';

export default {
  name: 'AssociationOverview',
  components: {
    AppHeader
  },
  data() {
    return {
      loading: true,
      imageLoadedMap: {},
      // 种子数据里的头像为裸文件名，挂在这个前缀下
      minioBase: 'https://www.hblaoxie.com/minio/laoxie/leaders/',

      intro: null,
      historyList: [],
      orgNodes: [],
      leaders: [],
      objectives: [],
      honors: [],

      // 组织架构固定层级节点缺失时的兜底名称（正常由后台 / 种子 SQL 提供，避免连线图残缺）
      ORG_FALLBACK: {
        ASSEMBLY: '会员(代表)大会',
        COUNCIL: '理事会',
        SUPERVISORY: '监事会',
        PRESIDENT_OFFICE: '会长办公室',
        EXPERT_COMMITTEE: '专家顾问委员会',
        SECRETARIAT: '秘书处',
        MEDIATION_COMMITTEE: '人民调解委员会'
      },

      // 概况导航（scroll-spy 高亮）
      navItems: [
        { key: 'intro', icon: 'corporate_fare', label: '协会简介' },
        { key: 'history', icon: 'history_edu', label: '发展历程' },
        { key: 'structure', icon: 'account_tree', label: '组织架构' },
        { key: 'leadership', icon: 'groups', label: '领导风采' },
        { key: 'objectives', icon: 'flag', label: '宗旨与目标' },
        { key: 'achievements', icon: 'emoji_events', label: '荣誉成就' }
      ],
      activeSection: 'intro',

      // 领导详情（原位展开，不做整页跳转）
      detailLeader: null,
      avatarRevealed: false,
      flightSrcRect: null,
      flying: { show: false, style: {} },
      flyingSrc: '',

      // 荣誉证书大图预览
      lightboxUrl: ''
    };
  },
  computed: {
    // 协会简介正文：后台按「空行」分段
    introParagraphs() {
      const raw = this.intro && this.intro.paragraphs;
      if (!raw) return [];
      return raw.split(/\n\s*\n/).map((t) => t.trim()).filter(Boolean);
    },

    // 协会简介理念信息框：有值才渲染该条，全空则整块不显示
    introFacts() {
      const i = this.intro;
      if (!i) return [];
      return [
        { label: '协会愿景', content: i.vision },
        { label: '协会使命', content: i.mission },
        { label: '协会价值观', content: i.values },
        { label: '办会的四种精神', content: i.spirits },
        { label: '协会准则数字歌', content: i.mottoSong }
      ].filter((f) => f.content && String(f.content).trim());
    },

    // 组织架构：下属部门（可自由增删，前台 flex 自适应）
    orgDepartments() {
      return this.orgNodes.filter((n) => n.nodeLevel === 5);
    }
  },
  mounted() {
    this.fetchAbout();
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    async fetchAbout() {
      try {
        const [i, hist, org, l, o, h] = await Promise.all([
          aboutService.getIntro(),
          aboutService.getHistory(),
          aboutService.getOrg(),
          aboutService.getLeaders(),
          aboutService.getObjectives(),
          aboutService.getHonors()
        ]);
        this.intro = (i && i.data) || null;
        this.historyList = (hist && hist.data) || [];
        this.orgNodes = (org && org.data) || [];
        this.leaders = (l && l.data) || [];
        this.objectives = (o && o.data) || [];
        this.honors = (h && h.data) || [];
      } catch (e) {
        // 拉取失败时静默降级（页面其余部分仍可用）
        console.error('获取协会概况数据失败', e);
      } finally {
        this.loading = false;
      }
    },

    // 组织架构固定层级节点：按 nodeKey 取名；后台没配则回落内置名称，保证连线图不残缺
    orgName(key) {
      const hit = this.orgNodes.find((n) => n.nodeKey === key);
      return (hit && hit.name) || this.ORG_FALLBACK[key] || '';
    },

    // 兼容三种形态：裸文件名（种子）/ 内网前缀 URL（上传返回）/ 完整外网 URL
    resolveImg(url) {
      if (!url) return '';
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url.replace('http://8.148.4.196/minio', 'https://www.hblaoxie.com/minio');
      }
      // 站内静态资源（如 /images/about_intro.jpg）原样使用，不能拼 minio 前缀
      if (url.startsWith('/')) return url;
      return `${this.minioBase}${encodeURIComponent(url)}`;
    },

    onImageLoad(id) {
      this.imageLoadedMap[id] = true;
    },

    scrollToSection(id) {
      this.activeSection = id;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },

    handleScroll() {
      // 详情态下强制高亮「领导风采」，避免因区块高度变化导致高亮跳走
      if (this.detailLeader) {
        this.activeSection = 'leadership';
        return;
      }
      // 滚到页面底部时，末尾区块可能永远够不到顶部阈值 → 直接高亮最后一个
      const doc = document.documentElement;
      if (window.innerHeight + window.scrollY >= doc.scrollHeight - 4) {
        this.activeSection = this.navItems[this.navItems.length - 1].key;
        return;
      }
      const offset = 140;
      let current = this.navItems[0].key;
      for (const nav of this.navItems) {
        const el = document.getElementById(nav.key);
        if (el && el.getBoundingClientRect().top <= offset) {
          current = nav.key;
        }
      }
      this.activeSection = current;
    },

    // ---- 领导风采：详情展开 + 头像从卡片位连续放大移动到详情头像位 ----
    // 点击即进入详情；bio 为空时正文块不渲染（仅显示头像 / 姓名 / 职务）
    openLeaderDetail(item, evt) {
      if (!item) return;
      const card = evt && evt.currentTarget;
      const img = card ? card.querySelector('img') : null;
      this.flightSrcRect = img ? img.getBoundingClientRect() : null;
      this.flyingSrc = this.resolveImg(item.photoUrl);
      this.avatarRevealed = false;
      this.detailLeader = item;
      this.activeSection = 'leadership';

      this.$nextTick(() => {
        const target = this.$refs.detailAvatarRef;
        const s = this.flightSrcRect;
        if (!target || !s) {
          this.flying.show = false;
          this.avatarRevealed = true;
          return;
        }
        const t = target.getBoundingClientRect();
        // 起始帧：克隆元素停在卡片头像处
        this.flying = {
          show: true,
          style: {
            position: 'fixed',
            left: `${s.left}px`,
            top: `${s.top}px`,
            width: `${s.width}px`,
            height: `${s.height}px`,
            borderRadius: '24px',
            transition: 'none',
            zIndex: 60
          }
        };
        // 双 rAF 保证起始帧已绘制，再施加过渡到详情头像位
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.flying.style = {
              ...this.flying.style,
              transition:
                'left .5s cubic-bezier(.22,.61,.36,1), top .5s cubic-bezier(.22,.61,.36,1), ' +
                'width .5s cubic-bezier(.22,.61,.36,1), height .5s cubic-bezier(.22,.61,.36,1), ' +
                'border-radius .5s ease',
              left: `${t.left}px`,
              top: `${t.top}px`,
              width: `${t.width}px`,
              height: `${t.height}px`,
              borderRadius: '32px'
            };
            window.setTimeout(() => {
              this.avatarRevealed = true;
              this.flying.show = false;
            }, 520);
          });
        });
      });
    },

    closeLeaderDetail() {
      this.detailLeader = null;
      this.avatarRevealed = false;
      this.flying.show = false;
      this.$nextTick(() => this.scrollToSection('leadership'));
    },

    // ---- 宗旨与目标：点击进入对应文章 ----
    goObjective(obj) {
      if (!obj || !obj.linkUrl) return;
      const url = obj.linkUrl.trim();
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank');
      } else if (url.startsWith('#')) {
        window.location.hash = url.slice(1);
      } else if (url.startsWith('/')) {
        this.$router.push(url).catch(() => {});
      } else {
        window.open(url, '_blank');
      }
    },

    // ---- 荣誉成就：点击查看证书/奖牌大图 ----
    goHonor(award) {
      if (!award || !award.imageUrl) return;
      this.lightboxUrl = this.resolveImg(award.imageUrl);
    },

    goToLogin() {
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
html {
  scroll-behavior: smooth;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
